/**
 * The theme follows the account, the way Discord's does: it is an account
 * setting (`settings.theme`), applied on every device at READY and live
 * when another device changes it (USER_SETTINGS_UPDATE). Live bug
 * 2026-10-28: a new device showed System for a Smoked account, and another
 * open tab kept the old theme. (The interface is English only, so there is
 * no language to follow.)
 *
 * The device keeps three things apart:
 * - `theme`: a choice made on this device (Settings, the palette). Showing
 *   the account's look never makes one: a device that never chose keeps
 *   not having chosen.
 * - `look-synced`: what the account said last. A signed-in reload starts in
 *   it (lib/accountLook.ts), so nothing flashes before READY.
 * - the console's `defaults.*` (store/consoleDefaults.ts), for whoever has
 *   not chosen, signed out. Signing out shows the device's own look again
 *   (QA 2026-10-28: a device that never chose stayed Smoked after a Smoked
 *   account signed out, whatever the console's default said).
 *
 * A choice made here since the account last spoke (the save failed, or it
 * was made on the sign-in screen) is newer than the account's: READY saves
 * it to the account instead of overwriting it.
 */
import type { BackgroundSetting, ThemeSetting, UserSettings } from '@neosocial/protocol';
import { LOOK_SYNCED_KEY } from '../lib/accountLook';
import { api } from '../lib/api';
import { storage } from '../lib/storage';
import { useConfig } from './config';
import { applyConsoleDefaults } from './consoleDefaults';
import { updateData, useData } from './data';
import {
  applyBackground,
  applyTheme,
  isBackgroundSetting as isBackground,
  setBackground,
  setTheme,
  useUi,
} from './ui';

/** The look the account keeps: the theme and the background (solid or the aurora, 2026-10-30). */
export interface Look {
  theme?: ThemeSetting;
  background?: BackgroundSetting;
}

const THEMES: readonly string[] = ['pearl', 'smoked', 'system'];

const isTheme = (v: unknown): v is ThemeSetting => THEMES.includes(v as string);

/** This device's own choices (`theme`, `background`), where it made them. */
function deviceLook(): Look {
  const theme = storage.get('theme');
  const background = storage.get('background');
  return {
    ...(isTheme(theme) ? { theme } : {}),
    ...(isBackground(background) ? { background } : {}),
  };
}

function synced(): Look {
  const s = storage.getJson<Look>(LOOK_SYNCED_KEY, {});
  return {
    ...(isTheme(s?.theme) ? { theme: s.theme } : {}),
    ...(isBackground(s?.background) ? { background: s.background } : {}),
  };
}

function markSynced(p: Look): void {
  const next: Look = { ...synced() };
  if (p.theme !== undefined) next.theme = p.theme;
  if (p.background !== undefined) next.background = p.background;
  storage.setJson(LOOK_SYNCED_KEY, next);
}

/** What this device chose that the account has not confirmed yet. */
export function pendingLook(): Look {
  const was = synced();
  const own = deviceLook();
  return {
    ...(own.theme !== undefined && own.theme !== was.theme ? { theme: own.theme } : {}),
    ...(own.background !== undefined && own.background !== was.background
      ? { background: own.background }
      : {}),
  };
}

/** Signing out: the next account signing in here must not get this one's unsaved choices. */
export function forgetPendingLook(): void {
  markSynced(deviceLook());
}

/** Shows a theme without making it this device's choice. */
function showTheme(theme: ThemeSetting): void {
  applyTheme(theme);
  if (useUi.getState().theme !== theme) useUi.getState().set({ theme });
}

/** Shows a background without making it this device's choice. */
function showBackground(background: BackgroundSetting): void {
  applyBackground(background);
  if (useUi.getState().background !== background) useUi.getState().set({ background });
}

/**
 * Signed out: the device's own look again. Its choice where it made one,
 * else the console's default (else System, and the aurora).
 */
export function showDeviceLook(): void {
  const own = deviceLook();
  showTheme(own.theme ?? 'system');
  showBackground(own.background ?? 'glow');
  const config = useConfig.getState().config;
  if (config) applyConsoleDefaults(config);
}

// saves of this tab still on their way: their echo must not undo a newer choice
let saving = 0;

/** Saves this device's pending choice; false when it could not be saved (it stays pending). */
async function push(): Promise<boolean> {
  const p = pendingLook();
  if (p.theme === undefined && p.background === undefined) return true;
  if (!useData.getState().settings) return false;
  saving++;
  try {
    const s = await api.me.patchSettings({ ...p });
    // a server from before the background answers without one: it is not asked again
    markSynced({ theme: s.theme, background: s.background ?? p.background });
    updateData((d) => ({ ...d, settings: s }));
    return true;
  } catch {
    return false;
  } finally {
    saving--;
  }
}

/** A theme chosen on this device: shown at once and saved to the account. */
export function chooseTheme(theme: ThemeSetting): Promise<boolean> {
  setTheme(theme);
  return push();
}

/** A background chosen on this device (Settings > Appearance): shown at once and saved to the account. */
export function chooseBackground(background: BackgroundSetting): Promise<boolean> {
  setBackground(background);
  return push();
}

/**
 * The account's settings arrived (READY) or changed (USER_SETTINGS_UPDATE):
 * show its theme and background, each the same way. At READY a choice this device made since it last heard
 * from the account wins and is saved; a live change made elsewhere wins
 * over an older unsaved one here, but not over a save of this tab still on
 * its way. A choice this device made before is replaced by the account's
 * newer one; a device that never chose still has not.
 */
export function applyAccountLook(
  s: UserSettings | null | undefined,
  why: 'ready' | 'update',
): void {
  if (!s) return;
  const p = pendingLook();
  const keepTheme = p.theme !== undefined && p.theme !== s.theme && (why === 'ready' || saving > 0);
  if (!keepTheme && isTheme(s.theme)) {
    if (storage.get('theme') !== null) storage.set('theme', s.theme);
    showTheme(s.theme);
    markSynced({ theme: s.theme });
  }
  const keepBackground =
    p.background !== undefined && p.background !== s.background && (why === 'ready' || saving > 0);
  if (!keepBackground && isBackground(s.background)) {
    if (storage.get('background') !== null) storage.set('background', s.background);
    showBackground(s.background);
    markSynced({ background: s.background });
  }
  if (why === 'ready' && saving === 0 && (keepTheme || keepBackground)) void push();
}
